import { ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { Storage } from '@ionic/storage-angular';
import { forkJoin } from 'rxjs';
import { ProfileService } from 'src/app/services/profile.service';
import { ExtraAddress, Profile } from '../../models/register.models';
import { OveralMessageComponent } from '../overal-message/overal-message.component';
import { AlertService } from '../alert/alert.service';
import { ModalService } from '../modal/modal.service';
import { ToastService } from '../toast/toast.service';
import { ProfileAddressFormComponent } from './profile-address-form/profile-address-form.component';
import { Regions } from '../../other/regions';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'fun-profile-addresses',
  templateUrl: './profile-addresses.component.html',
  styleUrls: ['./profile-addresses.component.scss']
})
export class ProfileAddressesComponent implements OnInit, OnChanges {

  @Input() profile: Profile;
  @Input() addresses: ExtraAddress[] = [];
  @Input() isMobile: boolean = false;
  @Input() embeddedMode: boolean = false; // Modo embebido: emite eventos en lugar de abrir modales
  @Output() deleteAddressEvent = new EventEmitter<ExtraAddress>();
  @Output() addressSaved = new EventEmitter<ExtraAddress[]>();
  @Output() editTaxAddressEvent = new EventEmitter<void>();
  @Output() addAddressEvent = new EventEmitter<void>(); // Para modo embebido
  @Output() editAddressEvent = new EventEmitter<ExtraAddress>(); // Para modo embebido

  loading: boolean = false;
  saving: boolean = false;

  // 1 = Dirección del DNI
  // 2 = Dirección censal
  // 3 = Dirección de residencia
  // 4 = Vivienda en propiedad
  // 5 = Vivienda de alquiler
  // 6 = Dirección laboral
  addressTypes = [
    { id: 1, name: this.translate.instant('EXTRA_ADDRESS.DNI'), icon: 'home' },
    { id: 2, name: this.translate.instant('EXTRA_ADDRESS.CENSAL'), icon: 'home' },
    { id: 3, name: this.translate.instant('EXTRA_ADDRESS.RESIDENCE'), icon: 'home' },
    { id: 4, name: this.translate.instant('EXTRA_ADDRESS.OWN'), icon: 'home' },
    { id: 5, name: this.translate.instant('EXTRA_ADDRESS.RENT'), icon: 'home' },
    { id: 6, name: this.translate.instant('EXTRA_ADDRESS.JOB'), icon: 'home' }
  ];

  regionMap: { [code: string]: string } = {};
  cityMap: { [id: string]: string } = {};
  private loadedRegions: Set<string> = new Set();

  constructor(
    private chdRef: ChangeDetectorRef,
    private storage: Storage,
    private profileService: ProfileService,
    private alertService: AlertService,
    private modalService: ModalService,
    private toastService: ToastService,
    private translate: TranslateService
  ) {
    this.buildRegionMap();
  }

  ngOnInit() {
    if (!this.profile) {
      this.loadProfile();
    } else {
      this.resolveLocationNames();
      this.chdRef.detectChanges();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.addresses || changes.profile) {
      this.resolveLocationNames();
    }
  }

  private buildRegionMap(): void {
    Regions.forEach(region => {
      this.regionMap[String(region.code)] = region.name;
    });
  }

  private resolveLocationNames(): void {
    const regionCodes: Set<string> = new Set();

    // From extra addresses (national only)
    if (this.addresses) {
      this.addresses.forEach(addr => {
        if (addr.region && (addr.country === 108 || !addr.country)) {
          regionCodes.add(String(addr.region));
        }
      });
    }

    // From profile tax address (national only)
    if (this.profile && (this.profile.taxCountry === 108 || !this.profile.taxCountry) && this.profile.taxRegion) {
      regionCodes.add(String(this.profile.taxRegion));
    }

    // Fetch cities for each unique region not yet loaded
    regionCodes.forEach(regionCode => {
      if (this.loadedRegions.has(regionCode)) return;
      this.loadedRegions.add(regionCode);
      this.profileService.getCities(108, regionCode).subscribe(
        (cities: any[]) => {
          cities.forEach(city => {
            this.cityMap[String(city.id || city.name)] = city.name;
          });
          this.chdRef.detectChanges();
        }
      );
    });
  }

  getRegionName(code: any): string {
    if (!code) return '';
    return this.regionMap[String(code)] || String(code);
  }

  getCityName(id: any): string {
    if (!id) return '';
    return this.cityMap[String(id)] || String(id);
  }

  loadProfile(): void {
    this.loading = true;
    this.storage.get('USERNAME').then(val => {
      if (!val) {
        this.loading = false;
        console.warn('No username found in storage');
        return;
      }
      this.profileService.getProfile(val).subscribe(
        (resp: Profile) => {
          this.profile = resp;
          this.addresses = resp.addresses || [];
          this.loading = false;
          this.resolveLocationNames();
          this.chdRef.detectChanges();
        },
        () => {
          this.loading = false;
          this.chdRef.detectChanges();
        }
      );
    });
  }

  hasProfileTaxAddress(): boolean {
    return this.profile && this.profile.taxAddress && this.profile.taxAddress.trim() !== '';
  }

  formatProfileTaxAddress(): string {
    if (!this.profile) return '';

    const parts: string[] = [];

    if (this.profile.taxAddress) {
      parts.push(this.profile.taxAddress);
    }
    if (this.profile.taxPostalCode) {
      parts.push(this.profile.taxPostalCode);
    }

    // Dirección nacional (España)
    if (this.profile.taxCountry === 108 || !this.profile.taxCountry) {
      if (this.profile.taxCity) {
        parts.push(this.getCityName(this.profile.taxCity));
      }
      if (this.profile.taxRegion) {
        parts.push(this.getRegionName(this.profile.taxRegion));
      }
    } else {
      // Dirección internacional
      if (this.profile.taxInternationalCity) {
        parts.push(this.profile.taxInternationalCity);
      }
      if (this.profile.taxInternationalRegion) {
        parts.push(this.profile.taxInternationalRegion);
      }
    }

    return parts.join(', ');
  }

  hasAddresses(): boolean {
    return (this.addresses && this.addresses.length > 0) || this.hasProfileTaxAddress();
  }

  getAddressTypeName(type: number): string {
    if (!type && type !== 0) return this.translate.instant('PROFILE_ADDRESSES.EXTRA_ADDRESS');
    const addressType = this.addressTypes.find(t => t.id === type);
    return addressType ? addressType.name : this.translate.instant('PROFILE_ADDRESSES.EXTRA_ADDRESS');
  }

  getAddressTypeIcon(type: number): string {
    if (!type && type !== 0) return 'home';
    const addressType = this.addressTypes.find(t => t.id === type);
    return addressType ? addressType.icon : 'home';
  }

  formatAddress(address: ExtraAddress): string {
    const parts: string[] = [];
    if (address.address) {
      parts.push(address.address);
    }
    if (address.postalCode) {
      parts.push(address.postalCode);
    }
    if (address.country === 108 || !address.country) {
      // Nacional
      if (address.city) {
        parts.push(this.getCityName(address.city));
      }
      if (address.region) {
        parts.push(this.getRegionName(address.region));
      }
    } else {
      // Internacional
      if (address.internationalCity) {
        parts.push(address.internationalCity);
      }
      if (address.internationalRegion) {
        parts.push(address.internationalRegion);
      }
    }
    return parts.length > 0 ? parts.join(', ') : this.translate.instant('PROFILE_ADDRESSES.NO_ADDRESS');
  }

  onEditTaxAddress(): void {
    this.editTaxAddressEvent.emit();
  }

  onAddAddress(): void {
    if (this.embeddedMode) {
      this.addAddressEvent.emit();
    } else {
      this.openAddressFormModal(null, false);
    }
  }

  onEditAddress(address: ExtraAddress): void {
    if (this.embeddedMode) {
      this.editAddressEvent.emit(address);
    } else {
      this.openAddressFormModal(address, true);
    }
  }

  private openAddressFormModal(address: ExtraAddress | null, isEdit: boolean): void {
    const modalParams = {
      profile: this.profile,
      address: address,
      isEdit: isEdit,
      isMobile: this.isMobile
    };

    const title = isEdit ? this.translate.instant('PROFILE_ADDRESSES.MODAL_EDIT_TITLE') : this.translate.instant('PROFILE_ADDRESSES.ADD_ADDRESS');

    this.modalService.showModal(ProfileAddressFormComponent, modalParams, title).subscribe((modal: any) => {
      modal.reduceHeight();
      modal.getChildComponent().subscribe((componentRef: ProfileAddressFormComponent) => {
        // Evento de guardar
        componentRef.saveEvent.subscribe(({ formData, selectedTypes }: { formData: any, selectedTypes: number[] }) => {
          const requests = [];
          let editedAddressUsed = false;

          for (var i = 0; i < selectedTypes.length; i++) {
            var type = selectedTypes[i];
            // Buscar si ya existe otra dirección con este tipo (excluyendo la que se edita)
            var existing = this.addresses.find(function (a) {
              return a.type === type && !(isEdit && address && a.id === address.id);
            });

            if (existing && existing.id) {
              // El tipo ya existe en otra dirección → actualizar esa dirección
              var model = Object.assign({}, formData, { id: existing.id, type: type });
              requests.push(this.profileService.updateExtraAddres(model));
            } else if (isEdit && address && address.id && !editedAddressUsed) {
              // Reutilizar la dirección que se está editando (puede cambiar de tipo)
              var model = Object.assign({}, formData, { id: address.id, type: type });
              requests.push(this.profileService.updateExtraAddres(model));
              editedAddressUsed = true;
            } else {
              // No existe dirección con este tipo → crear nueva
              var model = Object.assign({}, formData, { type: type });
              requests.push(this.profileService.createExtraAddres(model));
            }
          }

          if (requests.length === 0) {
            componentRef.showError();
            return;
          }

          forkJoin(requests).subscribe(
            (responses: any[]) => {
              this.addressSaved.emit(responses);
              componentRef.showSuccess(isEdit
                ? this.translate.instant('PROFILE_ADDRESSES.UPDATE_SUCCESS')
                : this.translate.instant('PROFILE_ADDRESSES.SAVE_SUCCESS'));
            },
            () => {
              componentRef.showError();
              this.chdRef.detectChanges();
            }
          );
        });

        // Evento de cancelar
        componentRef.cancelEvent.subscribe(() => {
          this.modalService.removeModal();
          this.chdRef.detectChanges();
        });
      });

      modal.onClose().subscribe(() => {
        this.chdRef.detectChanges();
      });
    });
  }

  onDeleteAddressConfirm(address: ExtraAddress): void {
    const modalParams = {
      text: `<h2>${this.translate.instant('PROFILE_ADDRESSES.DELETE_CONFIRM_TITLE')}</h2><p>${this.translate.instant('PROFILE_ADDRESSES.DELETE_CONFIRM_MSG')}</p>`,
      image: 1,
      btnList: [
        {
          type: 'primaryDark',
          index: 1,
          title: this.translate.instant('PROFILE_ADDRESSES.BTN_CONFIRM'),
        },
        {
          type: 'secondaryDark',
          index: 2,
          title: this.translate.instant('PROFILE_ADDRESSES.BTN_CANCEL'),
        },
      ],
    };
    this.alertService.showModal(OveralMessageComponent, modalParams, '', '', true).subscribe((modal: any) => {
      modal.getChildComponent().subscribe((componentRef: OveralMessageComponent) => {
        componentRef.btnEvent.subscribe((resp) => {
          this.alertService.removeModal();
          if (resp === 1) {
            if (address.id) {
              this.saving = true;
              this.chdRef.detectChanges();
              this.profileService.deleteExtraAddres(address.id).subscribe(
                () => {
                  this.addresses = this.addresses.filter(a => a.id !== address.id);
                  this.saving = false;
                  this.deleteAddressEvent.emit(address);
                  this.toastService.showToast(this.translate.instant('PROFILE_ADDRESSES.DELETE_SUCCESS'), 'success', true);
                  this.chdRef.detectChanges();
                },
                () => {
                  this.saving = false;
                  this.toastService.showToast(this.translate.instant('PROFILE_ADDRESSES.DELETE_ERROR'), 'warning');
                  this.chdRef.detectChanges();
                }
              );
            } else {
              this.addresses = this.addresses.filter(a => a !== address);
              this.deleteAddressEvent.emit(address);
              this.toastService.showToast(this.translate.instant('PROFILE_ADDRESSES.DELETE_SUCCESS'), 'success', true);
              this.chdRef.detectChanges();
            }
          }
          this.chdRef.detectChanges();
        });
      });
      modal.onClose().subscribe();
    });
    this.chdRef.detectChanges();
  }

}
